'use client';

import * as React from 'react';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { cn } from '@savvy-kit/lib/utils';

type StatBlockLayout = 'inline' | 'block';
type StatBlockVariant = 'default' | 'compact';

const DEFAULT_EMPTY_MESSAGE = '—';

type StatBlockProps = {
  readonly layout?: StatBlockLayout;
  readonly variant?: StatBlockVariant;
  /** Secondary control pinned to the block's top-right corner, revealed on hover or keyboard focus. */
  readonly action?: React.ReactNode;
  readonly className?: string;
  readonly children: React.ReactNode;
};

type StatItemProps = {
  readonly label: React.ReactNode;
  readonly value?: React.ReactNode;
  readonly subtitle?: React.ReactNode;
  readonly feedback?: React.ReactNode;
  readonly empty?: boolean;
  readonly emptyMessage?: React.ReactNode;
  readonly loading?: boolean;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  readonly className?: string;
};

function flattenStatBlockChildren(children: React.ReactNode): React.ReactElement[] {
  const items: React.ReactElement[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(child)) return;
    if (child.type === React.Fragment) {
      items.push(...flattenStatBlockChildren(child.props.children));
      return;
    }
    items.push(child);
  });
  return items;
}

function StatBlockAction({ action }: { readonly action: React.ReactNode }) {
  if (!action) {
    return null;
  }
  return (
    <div
      data-slot="stat-block-action"
      className="absolute right-2 top-2 z-10 opacity-0 transition-opacity focus-within:opacity-100 group-hover/stat-block:opacity-100"
    >
      {action}
    </div>
  );
}

function StatBlock({ layout = 'inline', variant = 'default', action, className, children }: StatBlockProps) {
  const items = flattenStatBlockChildren(children);
  const count = items.length;

  if (process.env['NODE_ENV'] !== 'production') {
    if (layout === 'inline' && (count < 1 || count > 6)) {
      console.warn(`StatBlock: inline layout supports 1–6 items, received ${count}.`);
    }
    if (layout === 'block' && ![2, 4, 6].includes(count)) {
      console.warn(`StatBlock: block layout supports 2, 4, or 6 items, received ${count}.`);
    }
  }

  if (layout === 'inline') {
    return (
      <div
        data-slot="stat-block"
        data-layout="inline"
        data-variant={variant}
        className={cn(
          'group/stat-block relative flex items-stretch divide-x divide-light overflow-hidden rounded-md border border-light bg-card',
          className,
        )}
      >
        {items.map((item, i) => (
          <div key={i} className="flex-1">
            {item}
            {i === count - 1 ? <StatBlockAction action={action} /> : null}
          </div>
        ))}
      </div>
    );
  }

  const rows = Math.ceil(count / 2);
  return (
    <div
      data-slot="stat-block"
      data-layout="block"
      data-variant={variant}
      className={cn(
        'group/stat-block relative grid grid-cols-2 overflow-hidden rounded-md border border-light bg-card',
        className,
      )}
    >
      {items.map((item, i) => {
        const row = Math.floor(i / 2);
        const col = i % 2;
        const isLastRow = row === rows - 1;
        const isLeftCol = col === 0;
        return (
          <div key={i} className={cn(isLeftCol && 'border-r border-light', !isLastRow && 'border-b border-light')}>
            {item}
            {i === count - 1 ? <StatBlockAction action={action} /> : null}
          </div>
        );
      })}
    </div>
  );
}

function StatItem({
  label,
  value,
  subtitle,
  feedback,
  empty = false,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  loading = false,
  onClick,
  disabled = false,
  ariaLabel,
  className,
}: StatItemProps) {
  if (process.env['NODE_ENV'] !== 'production' && !empty && !loading && value === undefined) {
    console.warn('StatItem: `value` is required when `empty` and `loading` are not set.');
  }

  const isInteractive = Boolean(onClick);
  const showFeedback = feedback && !empty && !loading;

  const content = (
    <>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-secondary group-data-[variant=compact]/stat-block:text-[10px] [&_svg]:relative [&_svg]:-top-[0.15em] [&_svg]:ml-1 [&_svg]:inline-block [&_svg]:shrink-0 [&_svg]:align-middle [&_svg:not([class*='size-'])]:size-3">
        {label}
      </span>
      {loading ? (
        <Skeleton className="my-1 h-6 w-20 group-data-[variant=compact]/stat-block:my-0.5 group-data-[variant=compact]/stat-block:h-5 group-data-[variant=compact]/stat-block:w-16" />
      ) : (
        <span
          className={cn(
            'text-2xl leading-tight font-semibold group-data-[variant=compact]/stat-block:text-base',
            empty && emptyMessage === DEFAULT_EMPTY_MESSAGE ? 'text-muted-foreground' : 'text-dark',
          )}
        >
          {empty ? emptyMessage : value}
        </span>
      )}
      {subtitle ? (
        loading ? (
          <Skeleton className="h-4 w-24 group-data-[variant=compact]/stat-block:h-3.5 group-data-[variant=compact]/stat-block:w-20" />
        ) : (
          <span className="text-xs text-secondary">{subtitle}</span>
        )
      ) : null}
      {/* Flex, not block: Feedback is inline-flex, so a block wrapper would generate a
          line box and add the inherited line-height's leading below it. Wrapping is on
          because `feedback` takes any node, including several pills. */}
      {showFeedback ? (
        <div data-slot="stat-item-feedback" className="flex flex-wrap items-center gap-1">
          {feedback}
        </div>
      ) : null}
    </>
  );

  const baseClasses = cn(
    'flex h-full w-full flex-col gap-1 px-5 py-4',
    'group-data-[variant=compact]/stat-block:px-4 group-data-[variant=compact]/stat-block:py-3.5',
    className,
  );

  if (isInteractive) {
    return (
      <button
        type="button"
        data-slot="stat-item"
        data-empty={empty ? '' : undefined}
        data-loading={loading ? '' : undefined}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-busy={loading}
        className={cn(
          baseClasses,
          'text-left transition-colors cursor-pointer',
          'hover:bg-component-hover',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
          'disabled:cursor-default disabled:hover:bg-transparent',
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      data-slot="stat-item"
      data-empty={empty ? '' : undefined}
      data-loading={loading ? '' : undefined}
      // A labelled region gives the stat a name for assistive tech and for
      // getByRole('region', { name }); without ariaLabel it stays a plain div
      // rather than an unnamed region, which screen readers skip anyway.
      role={ariaLabel ? 'region' : undefined}
      aria-label={ariaLabel}
      aria-busy={loading}
      className={baseClasses}
    >
      {content}
    </div>
  );
}

export { StatBlock, StatItem };
export type { StatBlockProps, StatItemProps, StatBlockLayout, StatBlockVariant };
